import React, { useState, useEffect, useRef } from 'react';

const ContextMenu = ({ x, y, onClose, menuType, actions, data }) => {
  const menuRef = useRef(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Use capture phase to ensure we get the event first
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscKey);
    
    // Force a scroll into view on menu creation
    if (menuRef.current) {
      menuRef.current.scrollIntoView({ block: 'nearest' });
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  // Ensure menu stays within viewport
  useEffect(() => {
    if (menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      let adjustedX = x;
      let adjustedY = y;
      
      if (x + menuRect.width > windowWidth) {
        adjustedX = windowWidth - menuRect.width - 5;
      }
      
      if (y + menuRect.height > windowHeight) {
        adjustedY = windowHeight - menuRect.height - 5;
      }
      
      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  // Prevent bubbling of context menu events
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  // Render different menu items based on menu type
  const renderMenuItems = () => {
    switch (menuType) {
      case 'desktop':
        return (
          <>
            <MenuItem 
              icon="plus" 
              text="New Folder" 
              onClick={() => actions.createFolder()} 
            />
            <MenuItem 
              icon="file" 
              text="New Text Document" 
              onClick={() => actions.createFile('text')} 
            />
            <MenuDivider />
            <MenuItem 
              icon="th-large" 
              text="View" 
              submenu={[
                { text: "Large Icons", icon: "th-large", onClick: () => actions.changeView('largeIcons') },
                { text: "Small Icons", icon: "th", onClick: () => actions.changeView('smallIcons') },
                { text: "List", icon: "list", onClick: () => actions.changeView('list') }
              ]}
              onMouseEnter={() => setActiveSubmenu('view')}
              onMouseLeave={() => setActiveSubmenu(null)}
              isSubmenuOpen={activeSubmenu === 'view'}
            />
            <MenuItem 
              icon="sort" 
              text="Sort By" 
              submenu={[
                { text: "Name", icon: "font", onClick: () => actions.sortBy('name') },
                { text: "Date modified", icon: "calendar-alt", onClick: () => actions.sortBy('date') },
                { text: "Type", icon: "file", onClick: () => actions.sortBy('type') },
                { text: "Size", icon: "weight", onClick: () => actions.sortBy('size') }
              ]}
              onMouseEnter={() => setActiveSubmenu('sort')}
              onMouseLeave={() => setActiveSubmenu(null)}
              isSubmenuOpen={activeSubmenu === 'sort'}
            />
            <MenuDivider />
            <MenuItem 
              icon="sync" 
              text="Refresh" 
              onClick={() => actions.refresh()} 
            />
            <MenuDivider />
            <MenuItem 
              icon="desktop" 
              text="Display settings" 
              onClick={() => actions.openSettings('display')} 
            />
            <MenuItem 
              icon="paint-brush" 
              text="Personalize" 
              onClick={() => actions.openSettings('personalize')} 
            />
          </>
        );
        
      case 'window':
        return (
          <>
            <MenuItem 
              icon="arrows-alt" 
              text="Maximize" 
              onClick={() => actions.maximizeWindow(data.windowId)} 
              disabled={data.isMaximized}
            />
            <MenuItem 
              icon="minus" 
              text="Minimize" 
              onClick={() => actions.minimizeWindow(data.windowId)} 
            />
            <MenuItem 
              icon="expand" 
              text="Restore" 
              onClick={() => actions.restoreWindow(data.windowId)} 
              disabled={!data.isMaximized}
            />
            <MenuDivider />
            <MenuItem 
              icon="times" 
              text="Close" 
              onClick={() => actions.closeWindow(data.windowId)} 
            />
            <MenuDivider />
            <MenuItem 
              icon="th-large" 
              text="Arrange windows" 
              onClick={() => {
                if (data.arrangeWindows) {
                  data.arrangeWindows();
                } else if (actions.arrangeWindows) {
                  actions.arrangeWindows();
                }
              }} 
            />
          </>
        );
        
      case 'taskbar':
        return (
          <>
            {data.isOpen ? (
              <MenuItem 
                icon="window-restore" 
                text="Restore" 
                onClick={() => actions.focusWindow(data.appId)} 
              />
            ) : (
              <MenuItem 
                icon="play" 
                text="Open" 
                onClick={() => actions.launchApp(data.appId)} 
              />
            )}
            <MenuItem 
              icon="thumbtack" 
              text={data.isPinned ? "Unpin from taskbar" : "Pin to taskbar"} 
              onClick={() => actions.togglePin(data.appId)} 
            />
            {data.isOpen && (
              <MenuItem 
                icon="times" 
                text="Close window" 
                onClick={() => actions.closeWindow(data.appId)} 
              />
            )}
          </>
        );
        
      case 'file':
        return (
          <>
            <MenuItem 
              icon="folder-open" 
              text="Open" 
              onClick={() => actions.openFile(data.file)} 
            />
            <MenuItem 
              icon="eye" 
              text="Quick Look" 
              onClick={() => actions.quickLook(data.file)} 
            />
            <MenuDivider />
            <MenuItem 
              icon="copy" 
              text="Copy" 
              onClick={() => actions.copyFile(data.file)} 
            />
            <MenuItem 
              icon="cut" 
              text="Cut" 
              onClick={() => actions.cutFile(data.file)} 
            />
            <MenuItem 
              icon="trash-alt" 
              text="Delete" 
              onClick={() => actions.deleteFile(data.file)} 
            />
            <MenuItem 
              icon="i-cursor" 
              text="Rename" 
              onClick={() => actions.renameFile(data.file)} 
            />
            <MenuDivider />
            <MenuItem 
              icon="info-circle" 
              text="Properties" 
              onClick={() => actions.showProperties(data.file)} 
            />
          </>
        );

      case 'icon':
        return (
          <>
            <MenuItem 
              icon="play" 
              text="Open" 
              onClick={() => actions.openApp(data.app)} 
            />
            <MenuDivider />
            {data.app.isInDesktop ? (
              <MenuItem 
                icon="times" 
                text="Remove from Desktop" 
                onClick={() => actions.removeFromDesktop(data.app.id)} 
              />
            ) : (
              <MenuItem 
                icon="plus" 
                text="Add to Desktop" 
                onClick={() => actions.addToDesktop(data.app)} 
              />
            )}
            {data.app.isPinned ? (
              <MenuItem 
                icon="thumbtack" 
                text="Unpin from taskbar" 
                onClick={() => actions.unpinFromTaskbar(data.app.id)} 
              />
            ) : (
              <MenuItem 
                icon="thumbtack" 
                text="Pin to taskbar" 
                onClick={() => actions.pinToTaskbar(data.app)} 
              />
            )}
            {data.app.isInstalled && (
              <MenuItem 
                icon="trash-alt" 
                text="Uninstall" 
                onClick={() => actions.uninstallApp(data.app.id)} 
              />
            )}
            <MenuDivider />
            <MenuItem 
              icon="info-circle" 
              text="App info" 
              onClick={() => actions.showAppInfo(data.app)} 
            />
          </>
        );
        
      default:
        return <div>Menu type not supported</div>;
    }
  };

  return (
    <div 
      ref={menuRef}
      onContextMenu={handleContextMenu}
      style={{ 
        position: 'fixed',
        left: x,
        top: y,
        backgroundColor: 'rgba(32, 32, 36, 0.95)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)',
        borderRadius: '8px',
        minWidth: '240px',
        maxWidth: '320px',
        padding: '6px',
        zIndex: 9999,
        color: 'white',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        animation: 'contextMenuFadeIn 0.15s ease-out'
      }}
    >
      {renderMenuItems()}
      
      {/* Global styles for context menu animations */}
      <style>
        {`
          @keyframes contextMenuFadeIn {
            from { opacity: 0; transform: scale(0.98) translateY(-4px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          
          @keyframes submenuFadeIn {
            from { opacity: 0; transform: translateX(-5px); }
            to { opacity: 1; transform: translateX(0); }
          }
        `}
      </style>
    </div>
  );
};

const MenuItem = ({ icon, text, onClick, disabled, submenu, onMouseEnter, onMouseLeave, isSubmenuOpen }) => {
  const itemRef = useRef(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  
  const handleMouseEnter = () => {
    if (onMouseEnter) {
      onMouseEnter();
    }
    
    // Calculate submenu position
    if (itemRef.current && submenu) {
      const rect = itemRef.current.getBoundingClientRect();
      setSubmenuPosition({
        top: 0,
        left: rect.width
      });
    }
  };
  
  const handleMouseLeave = () => {
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  const handleClick = (e) => {
    if (disabled) return;
    if (onClick) {
      onClick();
      e.stopPropagation();
    }
  };
  
  return (
    <div 
      ref={itemRef}
      className={`context-menu-item ${disabled ? 'disabled' : ''}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ 
        display: 'flex',
        alignItems: 'center',
        padding: '8px 10px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        borderRadius: '6px',
        position: 'relative',
        transition: 'all 0.2s ease',
        margin: '2px 0',
        fontSize: '14px',
        fontWeight: '400',
        letterSpacing: '0.3px'
      }}
      onMouseOver={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
        }
      }}
      onMouseOut={(e) => {
        if (!disabled) {
        e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      {/* Icon */}
      {icon && (
        <div style={{ 
          width: '24px', 
          textAlign: 'center', 
          marginRight: '10px',
          color: 'rgba(255, 255, 255, 0.8)'
        }}>
          <i className={`fas fa-${icon}`}></i>
        </div>
      )}
      
      {/* Label */}
      <div style={{ flex: 1 }}>{text}</div>
      
      {/* Submenu indicator */}
      {submenu && (
        <div style={{ 
          marginLeft: '8px', 
          fontSize: '10px',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <i className="fas fa-chevron-right"></i>
        </div>
      )}
      
      {/* Render submenu if active */}
      {submenu && isSubmenuOpen && (
        <div 
          className="submenu"
          style={{
            position: 'absolute',
            top: submenuPosition.top,
            left: submenuPosition.left,
            backgroundColor: 'rgba(32, 32, 36, 0.95)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)',
            borderRadius: '8px',
            minWidth: '200px',
            padding: '6px',
            zIndex: 10000,
            color: 'white',
            animation: 'submenuFadeIn 0.2s ease-out',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {submenu.map((item, index) => (
            <div 
              key={index}
              className="submenu-item"
              onClick={item.onClick}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                padding: '8px 10px',
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'all 0.15s ease',
                margin: '2px 0',
                fontSize: '14px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {item.icon && (
                <div style={{ 
                  width: '24px', 
                  textAlign: 'center', 
                  marginRight: '10px',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}>
                  <i className={`fas fa-${item.icon}`}></i>
                </div>
              )}
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MenuDivider = () => (
  <div style={{ 
      height: '1px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '5px 0', 
    width: '100%' 
  }} />
);

export default ContextMenu; 